import type {
  PropertyAdEditorAutosaveValues,
  PropertyAdEditorFormValues,
  PropertyAdInProgressAutosaveFields,
  PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';

import { getPropertyAdAmenityValues, PROPERTY_AD_AMENITY_FIELDS } from './propertyAdAmenities';

const areHandoversEqual = (
  current: PropertyAdEditorAutosaveValues['handover'],
  saved: PropertyAdEditorAutosaveValues['handover']
) =>
  current === saved ||
  (current !== null &&
    saved !== null &&
    current.quarter === saved.quarter &&
    current.year === saved.year);

export const clonePropertyAdEditorAutosaveValues = (
  values: PropertyAdEditorAutosaveValues
): PropertyAdEditorAutosaveValues => ({
  responsibleUserId: values.responsibleUserId,
  activityKind: values.activityKind,
  transactionType: values.transactionType,
  propertyKind: values.propertyKind,
  currency: values.currency,
  price: values.price,
  sellingPrice: values.sellingPrice,
  completionStatus: values.completionStatus,
  toSeller: values.toSeller,
  toDeveloper: values.toDeveloper,
  postHandover: values.postHandover,
  handover: values.handover ? { ...values.handover } : null,
  defaultRentFrequency: values.defaultRentFrequency,
  priceYearly: values.priceYearly,
  priceMonthly: values.priceMonthly,
  priceWeekly: values.priceWeekly,
  priceDaily: values.priceDaily,
  addressEn: values.addressEn,
  nameEn: values.nameEn,
  title: values.title,
  placeId: values.placeId,
  coordinatesLat: values.coordinatesLat,
  coordinatesLong: values.coordinatesLong,
  locationId: values.locationId,
  districtId: values.districtId,
  buildingName: values.buildingName,
  multilisting: values.multilisting,
  addCommission: values.addCommission,
  currencyCommission: values.currencyCommission,
  topUp: values.topUp,
  topUpConditions: values.topUpConditions,
  description: values.description,
  builtUpArea: values.builtUpArea,
  beds: values.beds,
  baths: values.baths,
  floor: values.floor,
  furniture: values.furniture,
  renovation: values.renovation,
  occupancy: values.occupancy,
  rentedTill: values.rentedTill,
  view: [...values.view],
  ...getPropertyAdAmenityValues(values),
});

export const getPropertyAdEditorAutosaveValues = (
  values: PropertyAdEditorFormValues
): PropertyAdEditorAutosaveValues => {
  const description = values.description.trim();

  return clonePropertyAdEditorAutosaveValues({
    ...values,
    description: description === '' ? null : description,
    topUpConditions: values.topUpConditions === '' ? null : values.topUpConditions,
  });
};

export const getPropertyAdEditorPersistedAutosaveValues = (
  workspace: Pick<
    PropertyAdInProgressWorkspace,
    'completionStatus' | 'currency' | 'currencyCommission' | 'multilisting'
  >,
  hydratedValues: PropertyAdEditorFormValues
): PropertyAdEditorAutosaveValues => ({
  // Keep backend nulls for fields whose UI hydration supplies a visible default.
  ...getPropertyAdEditorAutosaveValues(hydratedValues),
  completionStatus: workspace.completionStatus ?? null,
  currency: workspace.currency ?? null,
  currencyCommission: workspace.currencyCommission ?? null,
  multilisting: workspace.multilisting ?? null,
});

export const buildPropertyAdEditorAutosaveDiff = (
  current: PropertyAdEditorAutosaveValues,
  lastSaved: PropertyAdEditorAutosaveValues,
  responsibleDefaultUserId?: string
): PropertyAdInProgressAutosaveFields => {
  const diff: PropertyAdInProgressAutosaveFields = {};

  if (current.responsibleUserId !== lastSaved.responsibleUserId) {
    diff.responsibleUserId = current.responsibleUserId ?? responsibleDefaultUserId ?? null;
  }
  if (current.activityKind !== lastSaved.activityKind) diff.activityKind = current.activityKind;
  if (current.transactionType !== lastSaved.transactionType) {
    diff.transactionType = current.transactionType;
  }
  if (current.propertyKind !== lastSaved.propertyKind) diff.propertyKind = current.propertyKind;
  if (current.currency !== lastSaved.currency) diff.currency = current.currency;
  if (current.price !== lastSaved.price) diff.price = current.price;
  if (current.sellingPrice !== lastSaved.sellingPrice) {
    diff.sellingPrice = current.sellingPrice;
  }
  if (current.completionStatus !== lastSaved.completionStatus) {
    diff.completionStatus = current.completionStatus;
  }
  if (current.toSeller !== lastSaved.toSeller) diff.toSeller = current.toSeller;
  if (current.toDeveloper !== lastSaved.toDeveloper) diff.toDeveloper = current.toDeveloper;
  if (current.postHandover !== lastSaved.postHandover) {
    diff.postHandover = current.postHandover;
  }
  if (!areHandoversEqual(current.handover, lastSaved.handover)) {
    diff.handover = current.handover ? { ...current.handover } : null;
  }
  if (current.defaultRentFrequency !== lastSaved.defaultRentFrequency) {
    diff.defaultRentFrequency = current.defaultRentFrequency;
  }
  if (current.priceYearly !== lastSaved.priceYearly) diff.priceYearly = current.priceYearly;
  if (current.priceMonthly !== lastSaved.priceMonthly) diff.priceMonthly = current.priceMonthly;
  if (current.priceWeekly !== lastSaved.priceWeekly) diff.priceWeekly = current.priceWeekly;
  if (current.priceDaily !== lastSaved.priceDaily) diff.priceDaily = current.priceDaily;
  if (current.addressEn !== lastSaved.addressEn) diff.addressEn = current.addressEn;
  if (current.nameEn !== lastSaved.nameEn) diff.nameEn = current.nameEn;
  if (current.title !== lastSaved.title) diff.title = current.title;
  if (current.placeId !== lastSaved.placeId) diff.placeId = current.placeId;
  if (current.coordinatesLat !== lastSaved.coordinatesLat) {
    diff.coordinatesLat = current.coordinatesLat;
  }
  if (current.coordinatesLong !== lastSaved.coordinatesLong) {
    diff.coordinatesLong = current.coordinatesLong;
  }
  if (current.locationId !== lastSaved.locationId) diff.locationId = current.locationId;
  if (current.districtId !== lastSaved.districtId) diff.districtId = current.districtId;
  if (current.buildingName !== lastSaved.buildingName) {
    diff.buildingName = current.buildingName;
  }
  if (current.multilisting !== lastSaved.multilisting) {
    diff.multilisting = current.multilisting;
  }
  if (current.addCommission !== lastSaved.addCommission) {
    diff.addCommission = current.addCommission === null ? null : String(current.addCommission);
  }
  if (current.currencyCommission !== lastSaved.currencyCommission) {
    diff.currencyCommission = current.currencyCommission;
  }
  if (current.topUp !== lastSaved.topUp) diff.topUp = current.topUp;
  if (current.topUpConditions !== lastSaved.topUpConditions) {
    diff.topUpConditions = current.topUpConditions;
  }
  if (current.description !== lastSaved.description) {
    diff.description = current.description;
  }
  if (current.builtUpArea !== lastSaved.builtUpArea) {
    diff.builtUpArea = current.builtUpArea;
  }
  if (current.beds !== lastSaved.beds) diff.beds = current.beds;
  if (current.baths !== lastSaved.baths) diff.baths = current.baths;
  if (current.floor !== lastSaved.floor) diff.floor = current.floor;
  if (current.furniture !== lastSaved.furniture) diff.furniture = current.furniture;
  if (current.renovation !== lastSaved.renovation) diff.renovation = current.renovation;
  if (current.occupancy !== lastSaved.occupancy) diff.occupancy = current.occupancy;
  if (current.rentedTill !== lastSaved.rentedTill) diff.rentedTill = current.rentedTill;
  if (
    current.view.length !== lastSaved.view.length ||
    current.view.some((value, index) => value !== lastSaved.view[index])
  ) {
    diff.view = [...current.view];
  }
  PROPERTY_AD_AMENITY_FIELDS.forEach((field) => {
    if (current[field] !== lastSaved[field]) diff[field] = current[field];
  });

  return diff;
};

export const hasPropertyAdEditorAutosaveDiff = (
  diff: PropertyAdInProgressAutosaveFields
): boolean => Object.keys(diff).length > 0;
